import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, Signal, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyPopoverModule } from '@vendasta/galaxy/popover';
import { GalaxySnackbarModule, SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { WhatsappLoginComponent } from './whatsapp-login.component';
import { WhatsAppConnectionService } from './whatsapp-connection.service';
import { EnvironmentService } from '@galaxy/core';

type BadgeValue = {
  color: 'red' | 'green';
  i18nKey: string;
};

export const Status = {
  NOT_CONNECTED: 'not_connected',
  CONNECTED: 'connected',
  TOKEN_INVALID: 'token_invalid',
  UNKNOWN: 'unknown',
  UNKNOWN_ERROR: 'unknown_error',
  EVENT_SUBSCRIPTION_MISSING: 'event_subscription_missing',
} as const;

type Status = (typeof Status)[keyof typeof Status];

@Component({
  selector: 'inbox-whatsapp-config',
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    TranslateModule,
    GalaxyBadgeModule,
    MatProgressSpinnerModule,
    GalaxyAlertModule,
    GalaxyPopoverModule,
    GalaxySnackbarModule,
    WhatsappLoginComponent,
  ],
  templateUrl: './whatsapp-config.component.html',
  styleUrl: './whatsapp-config.component.scss',
})
export class WhatsAppConfigComponent {
  MessagingStatus = Status;

  accountID = input.required<string>();
  partnerID = input.required<string>();

  protected whatsappConnectionService = inject(WhatsAppConnectionService);
  private environmentService = inject(EnvironmentService);

  connectedPhoneNumber: Signal<string | null>;
  badge: Signal<BadgeValue>;
  userHasInsufficientPermissions = signal(false);

  constructor(private readonly snackbarService: SnackbarService) {
    this.connectedPhoneNumber = computed(() => {
      const data = this.whatsappConnectionService.connection();
      if (data && data.status) {
        return data.status.displayPhoneNumber ?? null;
      }
      return null;
    });

    this.badge = computed((): BadgeValue => {
      switch (this.whatsappConnectionService.status()) {
        case Status.CONNECTED:
          return {
            color: 'green',
            i18nKey: 'INBOX.SETTINGS.WHATSAPP_MESSAGES.BADGE.CONNECTED',
          };
        case Status.NOT_CONNECTED:
          return {
            color: 'red',
            i18nKey: 'INBOX.SETTINGS.WHATSAPP_MESSAGES.BADGE.NOT_CONNECTED',
          };
        case Status.EVENT_SUBSCRIPTION_MISSING:
          return {
            color: 'red',
            i18nKey: 'INBOX.SETTINGS.WHATSAPP_MESSAGES.BADGE.SETUP_STEPS_INCOMPLETE',
          };
        case Status.TOKEN_INVALID:
          return {
            color: 'red',
            i18nKey: 'INBOX.SETTINGS.WHATSAPP_MESSAGES.BADGE.TOKEN_INVALID',
          };
        case Status.UNKNOWN:
          return {
            color: 'red',
            i18nKey: 'INBOX.SETTINGS.WHATSAPP_MESSAGES.BADGE.UNKNOWN',
          };
        case Status.UNKNOWN_ERROR:
          return {
            color: 'red',
            i18nKey: 'INBOX.SETTINGS.WHATSAPP_MESSAGES.BADGE.UNKNOWN_ERROR',
          };
      }
    });

    effect(() => {
      const organizationID = this.accountID() || this.partnerID();
      if (!organizationID) {
        return;
      }
      this.whatsappConnectionService.organizationID.set(organizationID);
    });
  }

  loginSrc() {
    return `https://facebook-${this.environmentService.getEnvironmentString()}.apigateway.co/whatsapp/login`;
  }

  async disconnect() {
    try {
      const status = await this.whatsappConnectionService.disconnect();
      if (status !== '') {
        this.snackbarService.openSuccessSnack(status);
      }
    } catch (error: unknown) {
      if (typeof error === 'string') {
        this.snackbarService.openErrorSnack(error);
      }
    }
  }
}
